import { useEffect, useMemo, useState } from "react";
import { Navigate, useLocation, useParams } from "react-router-dom";
import { useShallowCalendarStore } from "../../store";
import {
  CalendarHeader,
  convertStringToCalendarView,
  EventCreateDialog,
  getRangeFromDateAndView,
  type CalendarView,
} from "@tellescope/calendar";
import { useAuth } from "@tellescope/tanstack-query";
import { set } from "date-fns/set";
import { makeCalendarRouteFromRangeAndView } from "./utils";
import { CalendarPageContent } from "./CalendarPageContent";
import { ALL_CALENDAR_VIEW_ID, useCalendarTableViews } from "../../hooks/useCalendarTableViews";
import { CalendarFilterDialog } from "./filters/CalendarFilterDialog";
import { CalendarPageTabsHeader } from "./CalendarPageTabsHeader";

export const CalendarPage = () => {
  const location = useLocation();
  const view = useMemo<CalendarView | undefined>(() => {
    const segments = location.pathname.split("/");
    const calendarSegmentIndex = segments.findIndex((segment) => segment === "calendar");
    if (
      calendarSegmentIndex === -1 ||
      segments[calendarSegmentIndex + 1] !== "view" ||
      calendarSegmentIndex >= segments.length - 3
    ) {
      return "month";
    }
    return convertStringToCalendarView(segments[calendarSegmentIndex + 3] ?? "");
  }, [location.pathname]);
  const {
    lastSelectedViewId,
    lastSelectedViewType,
    setLastSelectedViewType,
    setLastSelectedViewId,
    selectedTimezone,
    showAvailabilityOverlay,
    selectedUserIds,
  } = useShallowCalendarStore(
    "lastSelectedViewId",
    "lastSelectedViewType",
    "setLastSelectedViewType",
    "setLastSelectedViewId",
    "selectedTimezone",
    "showAvailabilityOverlay",
    "selectedUserIds"
  );

  const params = useParams();

  const dateFromParams = useMemo(() => {
    return set(new Date(), {
      year: params.year === undefined ? new Date().getFullYear() : Number(params.year),
      month: params.month === undefined ? (view === "year" ? 0 : new Date().getMonth()) : Number(params.month),
      date: params.day === undefined ? (view === "year" ? 1 : new Date().getDate()) : Number(params.day),
    });
  }, [params.year, params.month, params.day, view]);
  const { startDate, endDate } = useMemo(
    () =>
      view
        ? getRangeFromDateAndView(dateFromParams, view, { timezone: selectedTimezone })
        : { startDate: undefined, endDate: undefined },
    [dateFromParams, view, selectedTimezone]
  );

  const { currentUser } = useAuth();
  const viewsController = useCalendarTableViews();

  // useEffects are justified here because this store is a localStorage store that we want to sync to
  useEffect(() => {
    if (viewsController.viewId && lastSelectedViewId !== viewsController.viewId) {
      setLastSelectedViewId(viewsController.viewId);
    }
  }, [viewsController.viewId, setLastSelectedViewId, lastSelectedViewId]);
  useEffect(() => {
    if (view && view !== lastSelectedViewType) {
      setLastSelectedViewType(view);
    }
  }, [view, setLastSelectedViewType, lastSelectedViewType]);

  const [filterDialogOpen, setFilterDialogOpen] = useState(false);
  const currentUserId = currentUser?.id;
  const effectiveFilters = viewsController.effectiveFilters;

  const navigationOptions = useMemo(
    () => makeCalendarRouteFromRangeAndView(viewsController.viewId),
    [viewsController.viewId]
  );

  const [selectedAvailabilityStart, setSelectedAvailabilityStart] = useState<Date>();

  return (
    <div className="flex flex-col h-full min-h-0 overflow-hidden">
      <CalendarPageTabsHeader />
      {view && startDate && endDate ? (
        <>
          <CalendarHeader
            selectedViewType={view}
            startDate={startDate}
            endDate={endDate}
            navigationOptions={navigationOptions}
            referenceDate={dateFromParams}
            timezone={selectedTimezone}
            view={view}
            filters={effectiveFilters}
            currentUserId={currentUserId}
            savedViews={viewsController.views.map((v) => ({ id: v.id, title: v.title }))}
            selectedSavedViewId={viewsController.isAllView ? undefined : viewsController.viewId}
            onSelectSavedView={(id) => viewsController.setViewId(id ?? ALL_CALENDAR_VIEW_ID)}
            hasUnsavedFilterChanges={viewsController.hasUnsavedChanges}
            canCreateSavedView={viewsController.canCreate}
            canDeleteSavedView={viewsController.canDelete}
            onCreateSavedView={async (title) => {
              const newId = await viewsController.createView(title, viewsController.stagedFilters);
              viewsController.setViewId(newId);
            }}
            onDeleteSavedView={() => viewsController.deleteSelectedView()}
            onOpenFilterDialog={() => setFilterDialogOpen(true)}
            availabilityUserId={
              showAvailabilityOverlay && selectedUserIds.length === 1 ? selectedUserIds.at(0) : undefined
            }
            getAvailabilityRangeClick={(date) => () => setSelectedAvailabilityStart(date)}
          />
          <CalendarPageContent
            view={view}
            startDate={startDate}
            endDate={endDate}
            referenceDate={dateFromParams}
            filters={effectiveFilters}
            currentUserId={currentUserId}
          />
          <CalendarFilterDialog
            open={filterDialogOpen}
            onOpenChange={setFilterDialogOpen}
            filters={effectiveFilters}
            onApply={viewsController.applyFilters}
            onClearAll={() => {
              viewsController.resetStagedFilters();
              setFilterDialogOpen(false);
            }}
          />
          <EventCreateDialog
            open={!!selectedAvailabilityStart}
            onOpenChange={(open) => {
              if (!open) {
                setSelectedAvailabilityStart(undefined);
              }
            }}
            initialFormValues={{
              startDate: selectedAvailabilityStart,
            }}
            onSuccess={() => setSelectedAvailabilityStart(undefined)}
            timezone={selectedTimezone}
          />
        </>
      ) : (
        <Navigate to="/calendar" />
      )}
    </div>
  );
};
